import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Newspaper, Clock, ChevronLeft, ChevronRight, ExternalLink, X, Share2 } from 'lucide-react';
import Footer from '../../components/layout/Footer';
import SEO from '../../components/seo/SEO';
import { getNews, getNewsDates, getNewsCategories } from '../../api/client';
import type { ApiResponse } from '../../types/api';

interface NewsItem {
  id: number;
  title: string;
  description: string;
  summary: string;
  source_name: string;
  source_url: string;
  image_url: string;
  category: string;
  news_date: string;
  published_at: string;
}

export default function NewsListScreen(): React.JSX.Element {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [dates, setDates] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeShort, setActiveShort] = useState<number | null>(null);
  const shortsRef = useRef<HTMLDivElement>(null);
  const perPage = 20;

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    Promise.all([
      getNewsCategories().catch(() => ({ categories: [] })),
      getNewsDates().catch(() => ({ dates: [] })),
    ]).then(([catRes, dateRes]) => {
      if (!mounted) return;
      if (catRes.categories) setCategories(catRes.categories);
      if (dateRes.dates) {
        setDates(dateRes.dates);
        if (dateRes.dates.length > 0) {
          setSelectedDate(dateRes.dates[0]);
        } else {
          setLoading(false);
          setError('No news available yet.');
        }
      }
    });

    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    setLoading(true);
    setError('');

    const params: Record<string, string> = { date: selectedDate, page: String(page), per_page: String(perPage) };
    if (selectedCategory !== 'All') params.category = selectedCategory;

    getNews(params)
      .then((res: ApiResponse) => {
        setNews(res.data || []);
        setTotalPages(res.totalPages || 1);
        setLoading(false);
      })
      .catch((err: Error) => {
        setError(err.message || 'Failed to load news');
        setLoading(false);
      });
  }, [selectedDate, selectedCategory, page]);

  const dateIndex = dates.indexOf(selectedDate);

  const handlePrevDate = () => {
    if (dateIndex < dates.length - 1) {
      setSelectedDate(dates[dateIndex + 1]);
      setPage(1);
    }
  };

  const handleNextDate = () => {
    if (dateIndex > 0) {
      setSelectedDate(dates[dateIndex - 1]);
      setPage(1);
    }
  };

  const formatDate = (d: string) => {
    if (!d) return '';
    const date = new Date(d + 'T00:00:00');
    return date.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatShortDate = (d: string) => {
    if (!d) return '';
    const date = new Date(d + 'T00:00:00');
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const categoryColors: Record<string, string> = {
    'AI': 'text-blue-700 bg-blue-50',
    'AI Finance': 'text-emerald-700 bg-emerald-50',
    'AI Investments': 'text-violet-700 bg-violet-50',
    'AI Marketing': 'text-amber-700 bg-amber-50',
    'AI Layoffs': 'text-rose-700 bg-rose-50',
  };

  // Group by category for newspaper columns
  const byCategory: Record<string, NewsItem[]> = {};
  news.forEach((item) => {
    if (!byCategory[item.category]) byCategory[item.category] = [];
    byCategory[item.category].push(item);
  });

  // Pick lead story
  const leadStory = news.find(n => n.category === 'AI Finance') || news[0];
  const secondaryStories = news.filter(n => n.id !== leadStory?.id).slice(0, 2);
  const remainingStories = news.filter(n => n.id !== leadStory?.id && !secondaryStories.find(s => s.id === n.id));

  // Swipe handlers for shorts
  const handleShortSwipe = (e: React.TouchEvent | React.MouseEvent, direction: 'next' | 'prev') => {
    if (activeShort === null) return;
    const currentIndex = news.findIndex(n => n.id === activeShort);
    if (direction === 'next' && currentIndex < news.length - 1) {
      setActiveShort(news[currentIndex + 1].id);
    } else if (direction === 'prev' && currentIndex > 0) {
      setActiveShort(news[currentIndex - 1].id);
    } else if (direction === 'next' && currentIndex === news.length - 1) {
      setActiveShort(null);
    }
  };

  const activeItem = news.find(n => n.id === activeShort);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="AI Daily News"
        description="Daily AI news digest — Finance, Investments, Marketing, and Industry updates."
        canonicalPath="/news"
      />

      {/* ─── NEWSPAPER MASTHEAD ─── */}
      <header className="bg-white border-b-4 border-slate-900">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Newspaper className="w-6 h-6 text-amber-600" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-slate-500">Daily Digest</span>
          </div>
          <h1 className="text-4xl sm:text-6xl font-black text-slate-900 tracking-tight leading-none">
            AI DAILY
          </h1>
          <div className="mt-3 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-sm text-slate-500">
            <span className="font-serif italic">{formatDate(selectedDate)}</span>
            <span className="hidden sm:inline text-slate-300">|</span>
            <span>{news.length} Stories</span>
            <span className="hidden sm:inline text-slate-300">|</span>
            <span className="text-amber-600 font-semibold">AI Intelligence for Finance Professionals</span>
          </div>
        </div>
      </header>

      {/* ─── DATE & CATEGORY BAR ─── */}
      <div className="bg-slate-900 text-white sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between py-2.5">
            <div className="flex items-center gap-1">
              <button onClick={handlePrevDate} disabled={dateIndex >= dates.length - 1} className="p-1 rounded hover:bg-slate-700 disabled:opacity-30 transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <Calendar className="w-3.5 h-3.5 text-amber-400 ml-1" />
              <span className="text-xs font-medium ml-1 hidden sm:inline">{formatDate(selectedDate)}</span>
              <span className="text-xs font-medium ml-1 sm:hidden">{formatShortDate(selectedDate)}</span>
              <button onClick={handleNextDate} disabled={dateIndex <= 0} className="p-1 rounded hover:bg-slate-700 disabled:opacity-30 transition-colors">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
              <button onClick={() => { setSelectedCategory('All'); setPage(1); }} className={`px-2.5 py-1 rounded text-[11px] font-bold uppercase tracking-wide transition-colors whitespace-nowrap ${selectedCategory === 'All' ? 'bg-amber-500 text-white' : 'text-slate-300 hover:text-white'}`}>All</button>
              {categories.map((cat) => (
                <button key={cat} onClick={() => { setSelectedCategory(cat); setPage(1); }} className={`px-2.5 py-1 rounded text-[11px] font-bold uppercase tracking-wide transition-colors whitespace-nowrap ${selectedCategory === cat ? 'bg-amber-500 text-white' : 'text-slate-300 hover:text-white'}`}>{cat}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ─── MAIN NEWSPAPER CONTENT ─── */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="space-y-4 animate-pulse">
            <div className="h-96 bg-slate-200 rounded" />
            <div className="grid md:grid-cols-3 gap-4">
              <div className="h-48 bg-slate-200 rounded" />
              <div className="h-48 bg-slate-200 rounded" />
              <div className="h-48 bg-slate-200 rounded" />
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-500 font-medium">{error}</p>
          </div>
        ) : news.length === 0 ? (
          <div className="text-center py-20">
            <Newspaper className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-900">No news found</h3>
            <p className="text-slate-500">Try selecting a different date or category.</p>
          </div>
        ) : (
          <>
            {/* ─── LEAD STORY ─── */}
            {leadStory && (
              <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6 bg-white border border-slate-200">
                <Link to={`/news/${leadStory.id}`} className="block group">
                  <div className="grid md:grid-cols-5">
                    <div className="md:col-span-3 h-64 sm:h-80 md:h-96 overflow-hidden bg-slate-100 relative">
                      {leadStory.image_url ? (
                        <img src={leadStory.image_url} alt={leadStory.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Newspaper className="w-16 h-16 text-slate-300" /></div>
                      )}
                    </div>
                    <div className="md:col-span-2 p-5 sm:p-8 flex flex-col justify-center border-t md:border-t-0 md:border-l border-slate-200">
                      <span className={`inline-block self-start px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider mb-3 ${categoryColors[leadStory.category] || 'text-slate-600 bg-slate-100'}`}>{leadStory.category}</span>
                      <h2 className="text-2xl sm:text-3xl font-black text-slate-900 leading-tight mb-4 group-hover:text-amber-700 transition-colors">{leadStory.title}</h2>
                      <p className="text-sm text-slate-600 leading-relaxed mb-5 line-clamp-4">{leadStory.summary || leadStory.description}</p>
                      <div className="flex items-center justify-between mt-auto">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">{leadStory.source_name}</span>
                        <span className="flex items-center gap-1 text-xs text-slate-400"><Clock className="w-3 h-3" />{leadStory.published_at ? new Date(leadStory.published_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.section>
            )}

            {/* ─── SECONDARY STORIES ─── */}
            {secondaryStories.length > 0 && (
              <section className="grid md:grid-cols-2 gap-px bg-slate-200 border border-slate-200 mb-6">
                {secondaryStories.map((item, i) => (
                  <motion.article key={item.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="bg-white">
                    <Link to={`/news/${item.id}`} className="block group p-5 sm:p-6">
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider mb-2 ${categoryColors[item.category] || 'text-slate-600 bg-slate-100'}`}>{item.category}</span>
                          <h3 className="text-lg font-bold text-slate-900 leading-snug mb-2 group-hover:text-amber-700 transition-colors">{item.title}</h3>
                          <p className="text-sm text-slate-600 line-clamp-2 leading-relaxed mb-3">{item.summary || item.description}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{item.source_name}</span>
                            <a href={item.source_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[10px] font-bold text-amber-600 hover:text-amber-700 flex items-center gap-0.5 uppercase tracking-wide">Source <ExternalLink className="w-3 h-3" /></a>
                          </div>
                        </div>
                        {item.image_url && (
                          <div className="hidden sm:block w-24 h-24 flex-shrink-0 bg-slate-100 overflow-hidden">
                            <img src={item.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                          </div>
                        )}
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </section>
            )}

            {/* ─── MOBILE: SWIPE SHORTS SECTION ─── */}
            <section className="md:hidden mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">News Shorts</h3>
                <span className="text-[10px] text-slate-400">Swipe →</span>
              </div>
              <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-4 px-4 pb-2">
                {news.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveShort(item.id)}
                    className="flex-shrink-0 w-[200px] snap-start relative rounded-xl overflow-hidden bg-slate-900 aspect-[3/4]"
                  >
                    {item.image_url ? (
                      <img src={item.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    ) : null}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-left">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase mb-1.5 ${categoryColors[item.category]?.replace('text-', 'text-').replace('bg-', 'bg-white/20 text-') || 'text-white bg-white/20'}`}>
                        {item.category}
                      </span>
                      <h4 className="text-xs font-bold text-white leading-snug line-clamp-3">{item.title}</h4>
                    </div>
                  </button>
                ))}
              </div>
            </section>

            {/* ─── REMAINING STORIES (Desktop 3-col, Mobile list) ─── */}
            {remainingStories.length > 0 && (
              <section className="bg-white border border-slate-200 mb-6">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">More Stories</h3>
                </div>
                <div className="divide-y divide-slate-100 sm:divide-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-3">
                  {remainingStories.map((item, i) => (
                    <motion.article key={item.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }} className="p-4 sm:p-5 group hover:bg-slate-50 transition-colors">
                      <Link to={`/news/${item.id}`} className="block">
                        <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider mb-2 ${categoryColors[item.category] || 'text-slate-600 bg-slate-100'}`}>{item.category}</span>
                        <h4 className="text-sm font-bold text-slate-900 leading-snug mb-2 group-hover:text-amber-700 transition-colors line-clamp-2">{item.title}</h4>
                        <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed mb-3">{item.summary || item.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{item.source_name}</span>
                          <a href={item.source_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[10px] font-bold text-amber-600 hover:text-amber-700 flex items-center gap-0.5"><ExternalLink className="w-3 h-3" /></a>
                        </div>
                      </Link>
                    </motion.article>
                  ))}
                </div>
              </section>
            )}

            {/* ─── PAGINATION ─── */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 py-4">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="px-4 py-2 bg-white border border-slate-200 text-xs font-bold uppercase tracking-wide text-slate-700 hover:bg-slate-50 disabled:opacity-40 transition-colors">Previous</button>
                <span className="px-4 py-2 text-xs font-bold text-slate-500">Page {page} of {totalPages}</span>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-4 py-2 bg-white border border-slate-200 text-xs font-bold uppercase tracking-wide text-slate-700 hover:bg-slate-50 disabled:opacity-40 transition-colors">Next</button>
              </div>
            )}
          </>
        )}
      </main>

      {/* ─── MOBILE SHORTS OVERLAY ─── */}
      <AnimatePresence>
        {activeShort !== null && activeItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black"
            onClick={() => setActiveShort(null)}
          >
            {/* Close button */}
            <button
              onClick={() => setActiveShort(null)}
              className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 text-white"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Progress bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-white/20 z-50">
              <motion.div
                className="h-full bg-amber-500"
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 5, ease: 'linear' }}
                key={activeShort}
              />
            </div>

            {/* Short content */}
            <div
              ref={shortsRef}
              className="h-full flex flex-col justify-end p-6 relative"
              onTouchStart={(e) => {
                const startX = e.touches[0].clientX;
                const handleTouchEnd = (ev: TouchEvent) => {
                  const endX = ev.changedTouches[0].clientX;
                  if (startX - endX > 50) handleShortSwipe(e, 'next');
                  else if (endX - startX > 50) handleShortSwipe(e, 'prev');
                  document.removeEventListener('touchend', handleTouchEnd);
                };
                document.addEventListener('touchend', handleTouchEnd);
              }}
            >
              {/* Background image */}
              {activeItem.image_url && (
                <img
                  src={activeItem.image_url}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-40"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />

              {/* Content */}
              <div className="relative z-10">
                <span className={`inline-block px-2.5 py-1 rounded text-[10px] font-bold uppercase mb-3 ${categoryColors[activeItem.category] || 'text-white bg-white/20'}`}>
                  {activeItem.category}
                </span>
                <h2 className="text-xl font-black text-white leading-tight mb-3">{activeItem.title}</h2>
                <p className="text-sm text-slate-200 leading-relaxed mb-4">{activeItem.summary || activeItem.description}</p>
                <div className="flex items-center gap-3">
                  <a
                    href={activeItem.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-600 transition-colors"
                  >
                    Read Full Story <ExternalLink className="w-4 h-4" />
                  </a>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(window.location.origin + '/news/' + activeItem.id);
                    }}
                    className="p-2.5 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-4 text-xs text-slate-400">
                  <span>{activeItem.source_name}</span>
                  <span>{news.findIndex(n => n.id === activeShort) + 1} / {news.length}</span>
                </div>
              </div>

              {/* Swipe hint */}
              <div className="absolute top-1/2 left-2 -translate-y-1/2 text-white/30">
                <ChevronLeft className="w-8 h-8" />
              </div>
              <div className="absolute top-1/2 right-2 -translate-y-1/2 text-white/30">
                <ChevronRight className="w-8 h-8" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
